import {
  CommonModule,
  DecimalPipe,
  LocationStrategy,
  PathLocationStrategy,
} from "@angular/common";
import {
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXsrfConfiguration,
} from "@angular/common/http";
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  NgModule,
  importProvidersFrom,
  inject,
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  RouterModule,
  Routes,
  provideRouter,
  withNavigationErrorHandler,
} from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
// import ngx-translate for i18n support
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { OAuthModule } from "angular-oauth2-oidc";
import { MarkdownModule } from "ngx-markdown";
// primeng module imports
import { AccordionModule } from "primeng/accordion";
import { AvatarModule } from 'primeng/avatar';
import { ConfirmationService, MessageService } from "primeng/api";
import { AutoCompleteModule } from "primeng/autocomplete";
import { BlockUIModule } from "primeng/blockui";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { ButtonGroupModule } from "primeng/buttongroup";
import { CardModule } from "primeng/card";
import { CheckboxModule } from "primeng/checkbox";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DataViewModule } from "primeng/dataview";
import { DividerModule } from "primeng/divider";
import { SelectModule } from "primeng/select";
import { FileUploadModule } from "primeng/fileupload";
import { InputNumberModule } from "primeng/inputnumber";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { InputTextModule } from "primeng/inputtext";
import { TextareaModule } from "primeng/textarea";
import { ListboxModule } from "primeng/listbox";
import { MenubarModule } from "primeng/menubar";
import { MessageModule } from "primeng/message";
import { MultiSelectModule } from "primeng/multiselect";
import { PanelModule } from "primeng/panel";
import { PanelMenuModule } from "primeng/panelmenu";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { RatingModule } from "primeng/rating";
import { ScrollTopModule } from "primeng/scrolltop";
import { ScrollPanelModule } from "primeng/scrollpanel";
import { SkeletonModule } from "primeng/skeleton";
import { StepsModule } from "primeng/steps";
import { TableModule } from "primeng/table";
import { TabsModule } from "primeng/tabs";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from "primeng/tree";
import { ScrollerModule } from "primeng/scroller";
import { ShepherdService } from "angular-shepherd";
import * as PlotlyJS from 'plotly.js-dist-min';
import { PlotlyModule, PlotlyViaCDNModule } from 'angular-plotly.js';
// import lipidcompass apis
import {
  LipidCompassBackendApiModule,
  LipidCompassBackendConfiguration,
} from "../../modules/lipidcompass-backend-client";
import {
  LipidCompassDataImporterApiModule,
  LipidCompassDataImporterConfiguration,
} from "../../modules/lipidcompass-data-importer-client";
import {
  MzTabValidationApiModule,
  MzTabValidationConfiguration,
} from "../../modules/mztabm-validator-client";
import { deployment, environment } from "../environments/environment";
import { AdminControlledVocabularyComponent } from "./admin/admin-controlledvocabularies/admin-controlledvocabularies.component";
import { AdminCrossReferencesComponent } from "./admin/admin-crossreferences/admin-crossreferences.component";
import { AdminCvParametersComponent } from "./admin/admin-cvparameters/admin-cvparameters.component";
import { AdminDataimportJobsComponent } from "./admin/admin-dataimport-jobs/admin-dataimport-jobs.component";
import { AdminDataimportSubmissionComponent } from "./admin/admin-dataimport-submission/admin-dataimport-submission.component";
import { AdminLipidComponent } from "./admin/admin-lipid/admin-lipid.component";
import { AdminLipidMapsComponent } from "./admin/admin-lipidmaps/admin-lipidmaps.component";
import { AdminLipidquantityComponent } from "./admin/admin-lipidquantity/admin-lipidquantity.component";
import { AdminMztabresultComponent } from "./admin/admin-mztabresult/admin-mztabresult.component";
import { AdminStudyComponent } from "./admin/admin-study/admin-study.component";
import { AdminSwissLipidsComponent } from "./admin/admin-swisslipids/admin-swisslipids.component";
// custom project components
import { DataSubmitComponent } from "./data-submit/data-submit.component";
import { DefineStudyComponent } from "./data-submit/define-study/define-study.component";
import { SubmissionComponent } from "./data-submit/submission/submission.component";
import { UploadComponent } from "./data-submit/upload/upload.component";
import { ValidationComponent } from "./data-submit/validation/validation.component";
import { DatasetDetailComponent } from "./dataset/dataset-detail/dataset-detail.component";
import { PageNotFoundComponent } from "./errors/page-not-found/page-not-found.component";
// components
// import { SubmitComponent } from './submit/submit.component';
import { AuthInterceptor } from "./_helpers/auth-interceptor";
import { ChunkLoadErrorHandler } from "./_helpers/chunk-load-error.handler";
import { CuratorConsentGuard } from "./_helpers/curator-consent-guard";
import { HasRoleGuard } from "./_helpers/hasrole-guard";
import { ReviewTokenInterceptor } from "./_helpers/review-token-interceptor";
// pipes
import { HttperrorhandlerService } from "./_services/httperrorhandler.service";
// services
import { ChunkLoadRecoveryService } from "./_services/chunk-load-recovery.service";
import { ExcelService } from "./_services/excel.service";
import { NotificationService } from "./_services/notification.service";
import { SelectionService } from "./_services/selection.service";
import { UserService } from "./_services/user.service";
import {
  MatomoConsentRequirement,
  MatomoModule,
  MatomoRouterModule,
} from "ngx-matomo-client";
import { DatasetCorrectionsComponent } from "./dataset/dataset-corrections/dataset-corrections.component";
import { providePrimeNG } from "primeng/config";
import LifsMaterial from "../environments/theme";
import { CdkDragPlaceholder } from "@angular/cdk/drag-drop";
import { Ripple } from "primeng/ripple";
import { LoggingService } from "./_services/logging.service";
import { AdminCachesComponent } from "./admin/admin-caches/admin-caches.component";
import { AdminChebiComponent } from "./admin/admin-chebi/admin-chebi.component";
import { AdminNewsComponent } from "./admin/admin-news/admin-news.component";

// PlotlyViaWindowModule.setPlotlyVersion("1.58.3");
// PlotlyViaWindowModule.setPlotlyVersion("2.12.1");

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

// Configure application routes, responsible components, activation guards and login requirements
// `routes` is exported so a Karma spec can exercise the real route config end-to-end
// with RouterTestingHarness (see admin-role-guard.routes.spec.ts) -- this is otherwise
// invisible to a per-node HasRoleGuard unit test (final-review I-2, see below).
export const routes: Routes = [
  {
    path: "home",
    loadComponent() {
      return import("./home/home.component").then((m) => m.HomeComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: false,
      // role: "user",
      feature: "home",
    },
  },
  {
    // Full-page Landscape Explorer, linked from the home Lipidome Landscape
    // card. Public like /atlas/overview on the backend (no login), gated purely
    // by the lipidomeLandscape feature flag — same requiresLogin:false + HasRoleGuard
    // pattern as "home" and "news" below.
    path: "lipidome-landscape",
    loadComponent() {
      return import("./landscape/containers/landscape-explorer.component").then((m) => m.LandscapeExplorerComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: false,
      feature: "lipidomeLandscape",
    },
  },
  {
    // The Lipidome Landscape Explorer shipped as "Atlas Explorer" before
    // v1.0.78; keep the old path working for bookmarks and published links.
    path: "landscape-explorer",
    redirectTo: "lipidome-landscape",
    pathMatch: "full",
  },
  {
    // Reporting-resolution analysis view. Both backing endpoints
    // (/coreLipidome/groups, /coreLipidome/{groupKey}/depth) are public, so this
    // follows the same requiresLogin:false + HasRoleGuard pattern as
    // "landscape-explorer" above, gated purely by the coreLipidomes feature flag.
    path: "core-lipidomes",
    loadComponent() {
      return import("./corelipidome/containers/resolution-analysis.component").then(
        (m) => m.ResolutionAnalysisComponent
      );
    },
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: false,
      feature: "coreLipidomes",
    },
  },
  // {
  //   path: 'lipid',
  //   component: LipidTreeComponent,
  //   canActivate: [IsAuthenticatedGuard],
  //   data: { requiresLogin: true },
  // },
  //[queryParams]="{ minMass: lipid.exactMass, maxMass: lipid.exactMass, lipidLevel: lipid.lipidLevel, names: lipid.normalizedShorthandName, matchMode: 'EXACT', normalizeName: 'false', }"
  {
    path: "lipid",
     loadComponent() {
      return import("./lipid/lipid-search/lipid-search.component").then((m) => m.LipidSearchComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "user",
      feature: "search",
    },
  },
  {
    path: "lipid/:id",
    loadComponent() {
      return import("./lipid/lipid-summary/lipid-summary.component").then((m) => m.LipidSummaryComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "user",
      feature: "lipid",
    },
  },
  // {
  //   path: "facetsearch",
  //   component: FacetSearchComponent,
  //   canActivate: [HasRoleGuard],
  //   data: {
  //     requiresLogin: true,
  //     role: "user",
  //     featureEnabled: environment.features.facetsearch,
  //   },
  // },
  // {
  //   path: "mssearch",
  //   component: LipidMsSearchComponent,
  //   canActivate: [HasRoleGuard],
  //   data: {
  //     requiresLogin: true,
  //     role: "user",
  //     featureEnabled: environment.features.mssearch,
  //   },
  // },
  {
    path: "study",
    loadComponent() {
      return import("./study/study.component").then((m) => m.StudyComponent);
    },
    providers: [LoggingService],
    //component: StudyComponent,
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "user",
      feature: "study",
    },
  },
  {
    path: "study/:id",
    loadComponent() {
      return import("./study/study-detail/study-detail.component").then((m) => m.StudyDetailComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "user",
      feature: "study",
      // A consented reviewer session may reach this detail route without a Keycloak login (#226).
      reviewerAccessible: true,
    },
  },
  {
    path: "study/pid/:nativeId",
    loadComponent() {
      return import("./study/study-detail/study-detail.component").then((m) => m.StudyDetailComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "user",
      feature: "study",
    },
  },
  {
    path: "dataset",
    loadComponent() {
      return import("./dataset/dataset.component").then((m) => m.DatasetComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "user",
      feature: "dataset",
    },
  },
  {
    // must appear before dataset/:id
    path: "dataset/compare",
    loadComponent() {
      return import("./dataset/dataset-compare/dataset-compare.component").then((m) => m.DatasetCompareComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "user",
      // Gated by datasetComparison (not dataset) so the comparison view can be
      // toggled independently of dataset browsing — matching the Dataset Compare
      // docs page (docs.config.ts) which is also gated by datasetComparison.
      feature: "datasetComparison",
    },
  },
  {
    path: "dataset/:id",
    component: DatasetDetailComponent,
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "user",
      feature: "dataset",
      // A consented reviewer session may reach this detail route without a Keycloak login (#226).
      reviewerAccessible: true,
    },
  },
  {
    path: "dataset/pid/:nativeId",
    component: DatasetDetailComponent,
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "user",
      feature: "dataset",
    },
  },
  // {
  //   path: "quantities",
  //   component: LipidQuantitySearchComponent,
  //   canActivate: [HasRoleGuard],
  //   data: {
  //     requiresLogin: true,
  //     role: "user",
  //     featureEnabled: environment.features.quantities,
  //   },
  // },
  {
    path: "submit",
    loadComponent() {
      return import("./data-submit/data-submit.component").then((m) => m.DataSubmitComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "user",
      feature: "submit",
    },
    children: [
      {
        path: "submit",
        component: DataSubmitComponent,
        // redirectTo: "submit",
        pathMatch: "full",
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "user",
          feature: "submit",
        },
      },
      {
        path: "upload/:id",
        component: UploadComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "user",
          feature: "submit",
        },
      },
      {
        path: "validation/:id",
        component: ValidationComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "user",
          feature: "submit",
        },
      },
      {
        path: "study/:id",
        component: DefineStudyComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "user",
          feature: "submit",
        },
      },
      {
        path: "submission/:id",
        component: SubmissionComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "user",
          feature: "submit",
        },
      },
    ],
  },
  {
    path: "review/:privateReviewUuid",
    loadComponent() {
      return import("./data-review/data-review.component").then((m) => m.DataReviewComponent);
    },
    providers: [LoggingService],
    //canActivate: [HasRoleGuard],
    data: {
      requiresLogin: false,
      //role: "user",
      feature: "review",
    },
  },
  {
    path: "mztabm",
    loadChildren: () =>
      import("../../modules/mztabm-editor/mztabm-editor.module").then(
        (m) => m.MztabmEditorModule
      ),
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "user",
      feature: "mztab",
    },
  },
  {
    path: "mztabm-v2",
    redirectTo: "mztabm",
    pathMatch: "full",
  },
  // {
  //   path: "literature",
  //   component: LipidLiteratureSearchComponent,
  //   canActivate: [HasRoleGuard],
  //   data: {
  //     requiresLogin: true,
  //     role: "user",
  //     featureEnabled: environment.features.literature,
  //   },
  // },
  {
    path: "guide",
    loadComponent() {
      return import("./docs/docs.component").then((m) => m.DocsComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: { requiresLogin: false, feature: "docs" },
  },
  {
    path: "guide/:topicId",
    loadComponent() {
      return import("./docs/docs.component").then((m) => m.DocsComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: { requiresLogin: false, feature: "docs" },
  },
  { path: "help", redirectTo: "guide", pathMatch: "full" },
  {
    path: "info",
    loadComponent() {
      return import("./info/info.component").then((m) => m.InfoComponent);
    },
    providers: [LoggingService],
    // canActivate: [HasRoleGuard],
    data: {
      requiresLogin: false,
      // role: "user",
      feature: "info",
    },
  },
  {
    path: "news",
    loadComponent() {
      return import("./news/news.component").then((m) => m.NewsComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: false,
      feature: "news",
    },
  },
  {
    path: "admin",
    loadComponent() {
      return import("./admin/admin.component").then((m) => m.AdminComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      // "curator" here is the shell/floor gate, not a grant to the whole section: this
      // parent's own canActivate fires for ANY fresh deep-link into /admin/* (verified with
      // RouterTestingHarness -- Angular runs a route's canActivate whenever that segment is
      // newly activated, including ancestors on a direct navigation, not just the leaf).
      // Every child below still carries its own role (mostly "admin"), so widening this
      // floor to "curator" only ever admits a curator as far as a specific child also
      // permits (final-review I-2: dataImport/submissions is ADMIN+CURATOR on the backend).
      role: "curator",
      feature: "admin",
    },
    children: [
      {
        path: "news",
        component: AdminNewsComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "caches",
        component: AdminCachesComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "controlledVocabularies",
        component: AdminControlledVocabularyComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "crossReferences",
        component: AdminCrossReferencesComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "cvParameters",
        component: AdminCvParametersComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "lipids",
        component: AdminLipidComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "lipidQuantities",
        component: AdminLipidquantityComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "mzTabResults",
        component: AdminMztabresultComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "studies",
        component: AdminStudyComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "chebiEntries",
        component: AdminChebiComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "lipidMapsEntries",
        component: AdminLipidMapsComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "swissLipidsEntries",
        component: AdminSwissLipidsComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
      {
        path: "dataImport/submissions",
        component: AdminDataimportSubmissionComponent,
        // CuratorConsentGuard (design §5.2, #340 Task 10): admins who curate via this
        // admin-hosted route go through the same one-time consent gate as /curate.
        canActivate: [HasRoleGuard, CuratorConsentGuard],
        data: {
          requiresLogin: true,
          // Final-review I-2: the Publish and curation-toggle actions on this page are
          // ADMIN+CURATOR on the backend (SubmissionController#toggleCuration,
          // data-importer publish), so curators must be able to reach it too. "curator" is
          // the least-privileged role that admits both, per this app's role hierarchy
          // (anonymous<user<curator<admin) -- every admin account also carries curator
          // (docker/keycloak/lifs-realm.json), and HasRoleGuard/UserService#hasRole does an
          // exact-match lookup against the JWT roles list, so this is not a hierarchy
          // computed client-side, just the floor both roles satisfy.
          role: "curator",
          feature: "admin",
        },
      },
      {
        path: "dataImport/jobs",
        component: AdminDataimportJobsComponent,
        canActivate: [HasRoleGuard],
        data: {
          requiresLogin: true,
          role: "admin",
          feature: "admin",
        },
      },
    ],
  },
  {
    // The curator's dedicated workspace (design §5.1), separate from /admin,
    // hosting the same shared submission-curation table. "curator" is the
    // anonymous<user<curator<admin floor, so admins reach it too.
    // CuratorConsentGuard (design §5.2, #340 Task 10) runs AFTER HasRoleGuard
    // (login/role floor) and gates on the one-time consent capture.
    path: "curate",
    loadComponent() {
      return import("./curate/curate.component").then((m) => m.CurateComponent);
    },
    canActivate: [HasRoleGuard, CuratorConsentGuard],
    data: {
      requiresLogin: true,
      role: "curator",
      feature: "curate",
    },
  },
  {
    // The curator consent gate (design §5.2, #340 Task 10) -- reached via
    // CuratorConsentGuard's redirect from /curate and
    // /admin/dataImport/submissions, preserving the attempted URL as
    // `returnUrl`. Deliberately NOT itself guarded by CuratorConsentGuard
    // (that would redirect right back here).
    path: "curate/consent",
    loadComponent() {
      return import("./curate/curate-consent/curate-consent.component").then(
        (m) => m.CurateConsentComponent
      );
    },
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: true,
      role: "curator",
      feature: "curate",
    },
  },
  {
    path: "login",
    loadComponent() {
      return import("./login/login.component").then((m) => m.LoginComponent);
    },
    providers: [LoggingService],
    canActivate: [HasRoleGuard],
    data: {
      requiresLogin: false,
      feature: "login",
    },
  },
  { path: "", redirectTo: "home", pathMatch: "full" },
  {
    // Internal-only detour route used by BackendHealthService to force a
    // soft-reload of the current route once the backend recovers. Never
    // linked to from the UI.
    path: "__reload__",
    loadComponent() {
      return import("./shared/reload-anchor/reload-anchor.component").then((m) => m.ReloadAnchorComponent);
    },
  },
  { path: "**", component: PageNotFoundComponent },
];

PlotlyModule.plotlyjs = PlotlyJS;

@NgModule({
  exports: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    RouterModule, //.forRoot(routes, { enableTracing: environment.debug }), // <-- debugging purposes only
    LipidCompassBackendApiModule.forRoot(() => {
        return new LipidCompassBackendConfiguration({
            basePath: deployment.backendPath,
        });
    }),
    LipidCompassDataImporterApiModule.forRoot(() => {
        return new LipidCompassDataImporterConfiguration({
            basePath: deployment.dataImporterPath,
        });
    }),
    MzTabValidationApiModule.forRoot(() => {
        return new MzTabValidationConfiguration({
            basePath: deployment.validationPath,
        });
    }),
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    ToastModule,
    FormsModule,
    ReactiveFormsModule,
    OAuthModule.forRoot({
        resourceServer: {
            allowedUrls: environment.allowedUrls,
            sendAccessToken: true,
        },
    }),
    TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient],
        },
    }),
    AccordionModule,
    AutoCompleteModule,
    BlockUIModule,
    AvatarModule,
    ConfirmDialogModule,
    DividerModule,
    FileUploadModule,
    TreeModule,
    TableModule,
    TabsModule,
    BreadcrumbModule,
    MenubarModule,
    MessageModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    ListboxModule,
    ConfirmDialogModule,
    ButtonModule,
    ButtonGroupModule,
    MultiSelectModule,
    CardModule,
    PanelMenuModule,
    PanelModule,
    DataViewModule,
    CheckboxModule,
    RatingModule,
    StepsModule,
    SkeletonModule,
    TooltipModule,
    FontAwesomeModule,
    ScrollTopModule,
    ScrollPanelModule,
    ProgressSpinnerModule,
    ScrollerModule,
    MarkdownModule.forRoot({ loader: HttpClient }),
    MatomoModule.forRoot({
        trackerUrl: "https://lifs-tools.org/matomo",
        siteId: "13",
        acceptDoNotTrack: true,
        requireConsent: 'cookie' as MatomoConsentRequirement,
        disabled: !environment.trackingEnabled,
    }),
    MatomoRouterModule,
    //PlotlyViaWindowModule,
    PlotlyModule,
    //{
    //    version: "2.35.3"
    //    bundleName: null
    //}),
    CdkDragPlaceholder,
    Ripple
],
  providers: [
    provideRouter(
      routes,
      // Lazy routes load through loadComponent(), so a missing chunk surfaces
      // here as a NavigationError rather than reaching the global ErrorHandler.
      withNavigationErrorHandler((event) => {
        inject(ChunkLoadRecoveryService).handle(event.error);
      })
    ),
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      // Attaches X-Review-Token to backend API calls while in reviewer mode
      // (#226 Phase 7). Runs after AuthInterceptor; a reviewer is anonymous wrt
      // Keycloak so AuthInterceptor adds no bearer token.
      provide: HTTP_INTERCEPTORS,
      useClass: ReviewTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttperrorhandlerService,
      multi: true,
    },
    HasRoleGuard,
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    DecimalPipe,
    ExcelService,
    SelectionService,
    LoggingService,
    NotificationService,
    MessageService,
    ConfirmationService,
    ShepherdService,
    UserService,
    provideHttpClient(
      withInterceptorsFromDi(),
      withXsrfConfiguration({
        cookieName: "XSRF-TOKEN",
        headerName: "X-CSRF-TOKEN",
      })
    ),
    provideAnimationsAsync(),
    providePrimeNG({
        theme: {
            preset: LifsMaterial,
            options: {
                darkModeSelector: false || 'none',
                // cssLayer: {
                //     name: 'primeng',
                //     order: 'base, primeng, app'
                // }
            }
        },
        // Where every overlay panel (select, multiselect, autocomplete, datepicker, ...) is
        // attached. PrimeNG 21 defaults this to 'self', which renders the panel inline with
        // z-index:auto, so anything painted later in the DOM covers it -- and inside a
        // content-sized flex container the panel's own width can reflow its anchor
        // (issue #371). This moved out of `overlayOptions` in PrimeNG 21: the key there is
        // no longer read for append targeting, so it silently had no effect.
        overlayAppendTo: 'body'
    }),
    importProvidersFrom(
      //PlotlyViaWindowModule
      PlotlyViaCDNModule
    )
  ],
})
export class AppModule {}
